// eslint-disable-next-line node/no-deprecated-api
// @ts-ignore
// eslint-disable-next-line node/no-deprecated-api
import React from 'react';
import { Skeleton, Typography } from 'antd';
import Page from 'components/page';
import useGetTransaction from 'hooks/useGetTransaction';
import queryString from 'query-string';
import { useLocation, useParams } from 'react-router-dom';
import { getProperDate } from 'utils/date';
import { TransactionFeeSectionTitle, TransactionInfoContainer } from 'components/transaction.styles';
import { Space, Table } from 'antd/lib';
import { ColumnsType } from 'antd/es/table';
import { IFee } from 'interfaces/fee';
import { TransactionHelper } from './transactions/helper/transactions.helper';


const DataDisplay = ({ title, value }) => {
  return (
    <Space>
      <Typography.Text>{`${title}: `}</Typography.Text>
      <Typography.Text type="secondary" copyable>
        {value}
      </Typography.Text>
    </Space>
  );
};

const CURRENCY_CODE_BY_NUMBER = new Map(
  Object.entries({
    971: 'AFN',
    8: 'ALL',
    978: 'EUR',
    12: 'DZD',
    973: 'AOA',
    951: 'XCD',
    682: 'SAR',
    32: 'ARS',
    51: 'AMD',
    533: 'AWG',
    36: 'AUD',
    944: 'AZN',
    44: 'BSD',
    50: 'BDT',
    52: 'BBD',
    48: 'BHD',
    84: 'BZD',
    952: 'XOF',
    60: 'BMD',
    974: 'BYR',
    104: 'MMK',
    68: 'BOB',
    984: 'BOV',
    840: 'USD',
    977: 'BAM',
    72: 'BWP',
    986: 'BRL',
    96: 'BND',
    975: 'BGN',
    108: 'BIF',
    64: 'BTN',
    356: 'INR',
    132: 'CVE',
    116: 'KHR',
    950: 'XAF',
    124: 'CAD',
    990: 'CLF',
    152: 'CLP',
    156: 'CNY',
    170: 'COP',
    970: 'COU',
    174: 'KMF',
    976: 'CDF',
    188: 'CRC',
    191: 'HRK',
    931: 'CUC',
    192: 'CUP',
    532: 'ANG',
    208: 'DKK',
    818: 'EGP',
    222: 'SVC',
    784: 'AED',
    232: 'ERN',
    997: 'USN',
    230: 'ETB',
    242: 'FJD',
    608: 'PHP',
    960: 'XDR',
    270: 'GMD',
    981: 'GEL',
    936: 'GHS',
    292: 'GIP',
    320: 'GTQ',
    826: 'GBP',
    324: 'GNF',
    328: 'GYD',
    332: 'HTG',
    340: 'HNL',
    344: 'HKD',
    348: 'HUF',
    360: 'IDR',
    368: 'IQD',
    578: 'NOK',
    352: 'ISK',
    136: 'KYD',
    554: 'NZD',
    238: 'FKP',
    90: 'SBD',
    376: 'ILS',
    388: 'JMD',
    392: 'JPY',
    400: 'JOD',
    398: 'KZT',
    404: 'KES',
    417: 'KGS',
    414: 'KWD',
    426: 'LSL',
    710: 'ZAR',
    430: 'LRD',
    434: 'LYD',
    756: 'CHF',
    422: 'LBP',
    446: 'MOP',
    807: 'MKD',
    969: 'MGA',
    458: 'MYR',
    454: 'MWK',
    462: 'MVR',
    504: 'MAD',
    480: 'MUR',
    478: 'MRO',
    496: 'MNT',
    943: 'MZN',
    484: 'MXN',
    979: 'MXV',
    516: 'NAD',
    524: 'NPR',
    558: 'NIO',
    566: 'NGN',
    512: 'OMR',
    965: 'XUA',
    586: 'PKR',
    590: 'PAB',
    598: 'PGK',
    600: 'PYG',
    604: 'PEN',
    953: 'XPF',
    985: 'PLN',
    634: 'QAR',
    418: 'LAK',
    937: 'VEF',
    203: 'CZK',
    410: 'KRW',
    498: 'MDL',
    408: 'KPW',
    214: 'DOP',
    364: 'IRR',
    834: 'TZS',
    760: 'SYP',
    646: 'RWF',
    946: 'RON',
    643: 'RUB',
    882: 'WST',
    678: 'STD',
    654: 'SHP',
    941: 'RSD',
    690: 'SCR',
    694: 'SLL',
    702: 'SGD',
    994: 'XSU',
    706: 'SOS',
    144: 'LKR',
    748: 'SZL',
    938: 'SDG',
    728: 'SSP',
    752: 'SEK',
    947: 'CHE',
    948: 'CHW',
    968: 'SRD',
    764: 'THB',
    901: 'TWD',
    972: 'TJS',
    776: 'TOP',
    780: 'TTD',
    934: 'TMT',
    949: 'TRY',
    788: 'TND',
    800: 'UGX',
    980: 'UAH',
    940: 'UYI',
    858: 'UYU',
    860: 'UZS',
    548: 'VUV',
    704: 'VND',
    886: 'YER',
    262: 'DJF',
    967: 'ZMW',
    932: 'ZWL',
  })
);

const Transaction = () => {
  const { trxId, cardId } = useParams<{
    trxId: string;
    cardId: string;
  }>();

  const { search } = useLocation();
  const { cardHolderId, date, transactionType} = queryString.parse(search);

  const { transaction, isLoading } = useGetTransaction(trxId, cardId, cardHolderId as string, date as string, transactionType as string);

  function cleanCurrencyCode(cleanCurrencyCode: string) : string {
    // eslint-disable-next-line eqeqeq
    return (cleanCurrencyCode.charAt(0) == '0')? cleanCurrencyCode.slice(1): cleanCurrencyCode
  }


  const columns: ColumnsType<IFee> = [
    {
      dataIndex: 'type',
      key: 'type',
      title: 'Tipo',
    },
    {
      dataIndex: 'code',
      key: 'code',
      title: 'Codigo',
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'description',
      key: 'description',
      title: 'Descripcion',
      render: (_, record) => {
        return record?.description? record.description: 'No disponible';
      },
    },
    {
      dataIndex: 'amount',
      key: 'amount',
      title: 'Monto'
    },
  ];



  return (
    <Page title={`Transaccion #${trxId}`}>
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          {' '}
          <div>
            <TransactionInfoContainer>
              <DataDisplay title="ID" value={transaction?.authorization?.info?.transaction?.id} />
              <DataDisplay title="Presentacion" value={getProperDate(transaction?.authorization?.info?.transaction?.network_timestamp).concat('hs')} />
              <DataDisplay title="Tipo" value={TransactionHelper.getTransactionType(transaction?.authorization?.info?.transaction?.type)} />
              <DataDisplay title="Estado" value={transaction?.authorization?.approved || transaction?.authorization?.irc === '8444' ? 'Aprobada' : 'Rechazada'} />
              <DataDisplay title="Mensaje" value={(!transaction?.authorization?.approved || transaction?.authorization?.irc !== '8444')? transaction?.authorization?.info?.transaction?.display_message: 'Not available.'} />
              <DataDisplay title="Descripción" value={transaction?.authorization?.info?.merchant?.name} />
              <DataDisplay title="Modo de entrada" value={transaction?.authorization?.info?.pos_data_code?.reading_method[0]} />
              <DataDisplay title="Pais" value={transaction?.authorization?.info?.merchant?.country} />
              <DataDisplay title="Terminal" value={transaction?.authorization?.info?.merchant?.terminal_id} />
              <DataDisplay title="MCC" value={transaction?.authorization?.info?.merchant.mcc} />
              <DataDisplay title="DCC" value={transaction?.authorization?.info?.transaction?.dcc? 'Si': 'No'} />
              <DataDisplay title="Adquirente" value={transaction?.authorization?.info?.acquirer.code} />
              <DataDisplay title="3DS" value={transaction?.authorization?.info?.validations?.['3DS'] } />
              <DataDisplay title="Importe Original" value={new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency: CURRENCY_CODE_BY_NUMBER.get(transaction?.authorization?.info?.amounts?.original?.currency) || 'ARS',}).format(transaction?.authorization?.info.amounts.original.amount)} />
              <DataDisplay title="Impuestos" value={`$ ${transaction?.authorization?.info?.fee.reduce((acc: number, fee: any) => acc + fee.amount, 0)}`} />
              <DataDisplay title="Importe Final" value={
                new Intl.NumberFormat('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                }).format(transaction?.authorization?.info?.amounts?.billing?.amount)} />
            </TransactionInfoContainer>

            {transaction?.authorization?.info?.fee ? (
              <div>
                <TransactionFeeSectionTitle>Detalle Impuestos</TransactionFeeSectionTitle>
                <TransactionInfoContainer>
                  <DataDisplay title="Base" value={new Intl.NumberFormat('es-AR', {
                    style: 'currency',
                    currency: CURRENCY_CODE_BY_NUMBER.get(transaction?.authorization?.info?.amounts?.tax_base?.currency) || 'ARS',}).format(transaction?.authorization?.info?.amounts?.tax_base?.amount)} />
                  <DataDisplay title="Moneda" value={CURRENCY_CODE_BY_NUMBER.get(cleanCurrencyCode(transaction?.authorization?.info?.amounts?.tax_base?.currency))}/>
                  <DataDisplay title="Tipo de cambio" value={new Intl.NumberFormat('es-AR', {
                    style: 'currency',
                    currency: CURRENCY_CODE_BY_NUMBER.get(transaction?.authorization?.info?.amounts?.tax_base?.currency) || 'ARS',}).format(transaction?.authorization?.info?.amounts?.tax_base?.exchange_rate)} />
                </TransactionInfoContainer>
                <Table dataSource={transaction?.authorization?.info?.fee || []} columns={columns} loading={isLoading} pagination={false}/>
              </div>
            ): null}
          </div>
        </>
      )}
    </Page>
  );
};

export default Transaction;
